import type { LevelResult, PlayerProgress, SaveData } from "../domain/types";
import { getPlatformIdentity, platformRequest } from "./admin-access";
import {
  loadSave,
  clearSave,
  markSaveSynchronized,
  normalizeSaveData,
  readSaveMetadata,
  recordSaveError,
  writeSave,
} from "./save";

const GAME_KEY = "defense";
const SLOT_KEY = "campaign";

export type PlatformSyncState =
  | "idle"
  | "syncing"
  | "synced"
  | "local-only"
  | "offline"
  | "conflict-reconciled"
  | "error";

export interface PlatformSyncDiagnostic {
  readonly state: PlatformSyncState;
  readonly detail: string;
  readonly cloudVersion: number;
  readonly authenticated: boolean;
  readonly lastAttemptAt: string | null;
}

type SyncListener = (diagnostic: PlatformSyncDiagnostic) => void;

let diagnostic: PlatformSyncDiagnostic = {
  state: "idle",
  detail: "Cloud synchronization has not started.",
  cloudVersion: 0,
  authenticated: false,
  lastAttemptAt: null,
};
const listeners = new Set<SyncListener>();
let inFlight: Promise<PlatformSyncDiagnostic> | null = null;

function publish(update: PlatformSyncDiagnostic): PlatformSyncDiagnostic {
  diagnostic = update;
  for (const listener of listeners) listener(update);
  return update;
}

export function getPlatformSyncDiagnostic(): PlatformSyncDiagnostic {
  return diagnostic;
}

export function subscribeToPlatformSync(listener: SyncListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function number(value: number | undefined): number {
  return Math.max(0, Number.isFinite(value) ? Number(value) : 0);
}

function resultIsBetter(candidate: LevelResult, existing?: LevelResult): boolean {
  if (!existing) return true;
  if (candidate.victory !== existing.victory) return candidate.victory;
  if (candidate.score !== existing.score) return candidate.score > existing.score;
  if (candidate.livesRemaining !== existing.livesRemaining)
    return candidate.livesRemaining > existing.livesRemaining;
  if (candidate.wavesCompleted !== existing.wavesCompleted)
    return candidate.wavesCompleted > existing.wavesCompleted;
  return candidate.completedAt > existing.completedAt;
}

function mergeProgress(local: PlayerProgress, cloud: PlayerProgress): PlayerProgress {
  const bestResults = { ...cloud.bestResults };
  for (const [scenarioId, result] of Object.entries(local.bestResults)) {
    if (resultIsBetter(result, bestResults[scenarioId])) bestResults[scenarioId] = result;
  }
  const scenarioStars = { ...(cloud.scenarioStars ?? {}) };
  for (const [scenarioId, stars] of Object.entries(local.scenarioStars ?? {}))
    scenarioStars[scenarioId] = Math.max(scenarioStars[scenarioId] ?? 0, stars);
  const localStats = local.statistics;
  const cloudStats = cloud.statistics;
  return {
    ...cloud,
    ...local,
    completedLevels: [...new Set([...cloud.completedLevels, ...local.completedLevels])],
    unlockedTowers: [...new Set([...cloud.unlockedTowers, ...local.unlockedTowers])],
    bestResults,
    tutorialComplete: cloud.tutorialComplete || local.tutorialComplete,
    progressionCurrency: Math.max(
      number(cloud.progressionCurrency),
      number(local.progressionCurrency),
    ),
    royalSeals: Math.max(number(cloud.royalSeals), number(local.royalSeals)),
    scenarioStars,
    namedLoadouts: { ...(cloud.namedLoadouts ?? {}), ...(local.namedLoadouts ?? {}) },
    achievements: [...new Set([...(cloud.achievements ?? []), ...(local.achievements ?? [])])],
    statistics: {
      victories: Math.max(number(cloudStats?.victories), number(localStats?.victories)),
      defeats: Math.max(number(cloudStats?.defeats), number(localStats?.defeats)),
      bossesDefeated: Math.max(
        number(cloudStats?.bossesDefeated),
        number(localStats?.bossesDefeated),
      ),
      highestWave: Math.max(number(cloudStats?.highestWave), number(localStats?.highestWave)),
      totalScore: Math.max(number(cloudStats?.totalScore), number(localStats?.totalScore)),
    },
  };
}

/** Deterministic, monotonic reconciliation prevents either device from losing campaign unlocks. */
export function mergeDefenseSaves(
  localInput: SaveData,
  cloudInput: SaveData,
  preferCloudPreferences = false,
): SaveData {
  const local = normalizeSaveData(localInput);
  const cloud = normalizeSaveData(cloudInput);
  return normalizeSaveData({
    ...cloud,
    ...local,
    progress: mergeProgress(local.progress, cloud.progress),
    settings: preferCloudPreferences ? cloud.settings : local.settings,
    loadout: preferCloudPreferences ? cloud.loadout : local.loadout,
  });
}

function cloudSaveFrom(payload: unknown): { save: SaveData; version: number } | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;
  const root = payload as Record<string, unknown>;
  const record =
    root.save && typeof root.save === "object" && !Array.isArray(root.save)
      ? (root.save as Record<string, unknown>)
      : root;
  const data = record.save_data ?? record.saveData;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  return {
    save: normalizeSaveData(data as Partial<SaveData>),
    version: Math.max(0, Math.floor(Number(record.version) || 0)),
  };
}

async function upload(save: SaveData, expectedVersion: number): Promise<number> {
  const response = await platformRequest(`/api/v1/games/${GAME_KEY}/cloud-saves/${SLOT_KEY}`, {
    method: "PUT",
    body: JSON.stringify({ data: save, expectedVersion }),
  });
  if (response.status === 409)
    throw Object.assign(new Error("Cloud save changed."), { conflict: true });
  if (!response.ok) throw new Error(`Cloud save upload failed with HTTP ${response.status}.`);
  const cloud = cloudSaveFrom((await response.json()) as unknown);
  if (!cloud) throw new Error("Cloud save upload returned an invalid record.");
  return cloud.version;
}

async function synchronizeOnce(forceIdentity = false): Promise<PlatformSyncDiagnostic> {
  const attemptedAt = new Date().toISOString();
  publish({
    state: "syncing",
    detail: "Synchronizing campaign with LittleCastle…",
    cloudVersion: readSaveMetadata().cloudVersion,
    authenticated: false,
    lastAttemptAt: attemptedAt,
  });
  const identity = await getPlatformIdentity(forceIdentity);
  if (!identity.authenticated) {
    return publish({
      state: identity.status === "network-error" ? "offline" : "local-only",
      detail:
        identity.status === "network-error"
          ? "Platform unavailable; progress remains safe on this device."
          : "Sign in to store this campaign across devices.",
      cloudVersion: readSaveMetadata().cloudVersion,
      authenticated: false,
      lastAttemptAt: attemptedAt,
    });
  }

  const local = loadSave();
  const response = await platformRequest(`/api/v1/games/${GAME_KEY}/cloud-saves/${SLOT_KEY}`);
  if (response.status === 404) {
    const version = await upload(local, 0);
    markSaveSynchronized(version);
    return publish({
      state: "synced",
      detail: "Campaign stored in your LittleCastle account.",
      cloudVersion: version,
      authenticated: true,
      lastAttemptAt: attemptedAt,
    });
  }
  if (!response.ok) throw new Error(`Cloud save download failed with HTTP ${response.status}.`);
  const cloud = cloudSaveFrom((await response.json()) as unknown);
  if (!cloud) throw new Error("Cloud save download returned an invalid record.");
  const localMetadata = readSaveMetadata();
  const merged = mergeDefenseSaves(
    local,
    cloud.save,
    localMetadata.localRevision === 0 && !localMetadata.dirty,
  );
  const localChanged = JSON.stringify(merged) !== JSON.stringify(local);
  const cloudChanged = JSON.stringify(merged) !== JSON.stringify(cloud.save);
  if (localChanged) writeSave(merged, { markDirty: false });
  let version = cloud.version;
  if (cloudChanged || readSaveMetadata().dirty) version = await upload(merged, cloud.version);
  markSaveSynchronized(version);
  return publish({
    state: localChanged && cloudChanged ? "conflict-reconciled" : "synced",
    detail:
      localChanged && cloudChanged
        ? "Local and cloud progress were safely combined."
        : "Campaign synchronized with your LittleCastle account.",
    cloudVersion: version,
    authenticated: true,
    lastAttemptAt: attemptedAt,
  });
}

export function synchronizeDefensePlatform(force = false): Promise<PlatformSyncDiagnostic> {
  if (inFlight && !force) return inFlight;
  inFlight = synchronizeOnce(force)
    .catch(async (error: unknown) => {
      if (
        typeof error === "object" &&
        error !== null &&
        "conflict" in error &&
        (error as { conflict?: boolean }).conflict
      ) {
        inFlight = null;
        try {
          const retry = await synchronizeOnce();
          return publish({ ...retry, state: "conflict-reconciled" });
        } catch (retryError) {
          error = retryError;
        }
      }
      const message = error instanceof Error ? error.message : "Cloud synchronization failed.";
      recordSaveError(message);
      return publish({
        state: typeof navigator !== "undefined" && !navigator.onLine ? "offline" : "error",
        detail: `${message} Progress remains safe on this device.`,
        cloudVersion: readSaveMetadata().cloudVersion,
        authenticated: true,
        lastAttemptAt: new Date().toISOString(),
      });
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

async function submit(path: string, body: unknown): Promise<void> {
  const response = await platformRequest(path, { method: "PUT", body: JSON.stringify(body) });
  if (!response.ok && response.status !== 401)
    throw new Error(`Platform progress update failed with HTTP ${response.status}.`);
}

export async function recordDefenseResult(result: LevelResult): Promise<void> {
  await synchronizeDefensePlatform(true);
  const identity = await getPlatformIdentity();
  if (!identity.authenticated) return;
  const accountSave = loadSave();
  const achievements = new Set(accountSave.progress.achievements ?? []);
  if (result.victory) achievements.add("first-victory");
  if (result.victory && result.livesRemaining >= 10) achievements.add("master-maze");
  await Promise.all(
    [...achievements].map((key) =>
      submit(`/api/v1/games/${GAME_KEY}/achievements/${key}/progress`, { progress: 1 }),
    ),
  );
  if (result.victory) {
    const score = await platformRequest(
      `/api/v1/games/${GAME_KEY}/leaderboards/high-score/scores`,
      {
        method: "POST",
        body: JSON.stringify({
          score: result.score,
          metadata: {
            scenarioId: result.scenarioId,
            difficulty: result.difficulty,
            wavesCompleted: result.wavesCompleted,
          },
        }),
      },
    );
    if (!score.ok) throw new Error(`Leaderboard update failed with HTTP ${score.status}.`);
  }
  await submit(`/api/v1/games/${GAME_KEY}/statistics/me`, {
    statistics: {
      ...(accountSave.progress.statistics ?? {}),
      campaignStars: Object.values(accountSave.progress.scenarioStars ?? {}).reduce(
        (sum, stars) => sum + stars,
        0,
      ),
      scenariosCompleted: accountSave.progress.completedLevels.length,
      royalSeals: accountSave.progress.royalSeals ?? 0,
    },
  });
}

export async function resetDefenseCampaign(): Promise<{ ok: boolean; detail: string }> {
  const identity = await getPlatformIdentity(true);
  if (identity.authenticated) {
    try {
      const response = await platformRequest(`/api/v1/games/${GAME_KEY}/progression/me`, {
        method: "DELETE",
      });
      if (!response.ok && response.status !== 404)
        return {
          ok: false,
          detail: `Cloud reset failed with HTTP ${response.status}; no progress was erased.`,
        };
    } catch {
      return {
        ok: false,
        detail: "Cloud reset could not be confirmed; no progress was erased.",
      };
    }
  }
  clearSave();
  return {
    ok: true,
    detail: identity.authenticated
      ? "Local and cloud campaign progress were erased."
      : "Local campaign progress was erased.",
  };
}

if (typeof window !== "undefined")
  window.addEventListener("online", () => {
    void synchronizeDefensePlatform();
  });
